import React from 'react';
import { Modal } from 'react-bootstrap';
import { ServicesContext } from '~/services';
import { ErrorModal } from '~/components/Errors/ErrorModal';
import { MediaPreview } from '~/components/MediaPreview';
import { moment } from '~/vendorConfigs/moment';
import { enumValue, attestationSubmitSort, isProtected, gapIsPending } from '~/util';
import { BSModal } from '~/components/Bootstrap';
import { SubmitAnswerRow } from '~/components/Patient';
import { useRoleActions } from '~/util/useRoleActions';

interface SubmitProps {
    patient: PatientType;
    submission: PatientSubmissionType | null;
    attachments: MediaFileType[];
    handleLoading: (x: boolean) => void;
    disabled: boolean;
}
export function Submit({ patient, submission, attachments, handleLoading, disabled }: SubmitProps) {
    const context = React.useContext(ServicesContext);
    const roleCanSubmit = useRoleActions('allow_create_esubmission_risk_gap');
    const roleAttachmentRequired = useRoleActions(
        'override_risk_gap_esubmission_attachment_to_required'
    );
    const [showError, setShowError] = React.useState(false);
    const [show, setShow] = React.useState(false);
    const [error, setError] = React.useState({ title: '', description: '', showSubmit: false });

    const formattedDOV = React.useMemo(() => {
        if (!submission?.dov) return '';
        return moment(submission.dov).format('MM/DD/YYYY');
    }, [submission]);

    function formattedAnswerCount() {
        const counts = patient.risk_gaps.reduce(
            (acc, gap) => {
                if (gapIsPending(gap)) {
                    if (gap.submission_answer && !gap.is_added) {
                        acc.count += 1;
                    }
                    if ((gap.submission_answer || !isProtected(gap, submission)) && !gap.is_added) {
                        acc.total += 1;
                    }
                }

                return acc;
            },
            { count: 0, total: 0 }
        );

        return `${counts.count} out of ${counts.total} Addressed`;
    }

    function displayError(title: string, description: string, showSubmit: boolean) {
        setError({ title, description, showSubmit });
        setShowError(true);
    }

    function handleSubmit(override: boolean) {
        setShowError(false);
        let stopSubmit = false;
        const now = moment();
        const dov = moment(submission?.dov).format('YYYY-MM-DD');

        // Attachments are required always if user is MACL type, or if
        // all related orgs set is_attachment_required to true
        const isAttachmentRequired =
            context.user.isMACLUser() ||
            roleAttachmentRequired ||
            patient.organizations.every((x) => x.is_attachment_required !== false);

        // Stop Submit
        if (now.format('YYYY-MM-DD') < dov) {
            stopSubmit = true;
            displayError(
                'Date of Visit Error',
                'This eAttestation cannot be submitted until the patient visit has occurred.',
                false
            );
        } else if (isAttachmentRequired && attachments.length === 0) {
            stopSubmit = true;
            displayError(
                'Attachments Error',
                'This eAttestation cannot be submitted until attachments have been added.',
                false
            );
        }

        // Prompt with "Submit Anyway"
        if (!override) {
            if (dov < now.subtract(1, 'years').format('YYYY-MM-DD')) {
                stopSubmit = true;
                displayError(
                    'Date of Visit Error',
                    'The date of the patient’s visit occurred 1 year or more in the past, ' +
                        'are you sure you would like to submit this date as part of the current eAttestation?',
                    true
                );
            }
        }

        // Submit
        if (!stopSubmit) {
            setShow(true);
        }
    }

    async function submitAttestation() {
        handleLoading(true);

        try {
            await context.patient.submitSubmission({
                id: submission?.id,
                patient: {
                    id: patient.id,
                },
                gap_type: enumValue(['gap', 'type', 'Risk Gap']),
                dov: submission?.dov,
            });
        } catch {
            // empty catch?
        } finally {
            handleLoading(false);
            setShow(false);
            context.patient.load(patient.id);
        }
    }

    const canSubmit = !submission?.dov
        ? false
        : patient.risk_gaps.some((gap) => gap.submission_answer && gapIsPending(gap));

    return (
        <>
            <button
                type="submit"
                className="btn btn-info"
                onClick={() => handleSubmit(false)}
                disabled={!canSubmit || disabled}
            >
                Submit
            </button>
            <BSModal show={show} onHide={() => setShow(false)} aria-labelledby="att-submit-title">
                <Modal.Header closeButton>
                    <Modal.Title id="att-submit-title" role="heading" aria-level={1}>
                        Review Submission
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!roleCanSubmit && (
                        <div className="permissions-alert">
                            You don’t have permission to create eSubmissions on this record.
                        </div>
                    )}
                    <div className="row">
                        <div className="col fw-bold">Date of Visit:</div>
                    </div>
                    <div className="row">
                        <div className="ms-2 col-11">{formattedDOV}</div>
                    </div>
                    <div className="row mt-2">
                        <div className="col fw-bold">Conditions:</div>
                    </div>
                    <div className="row">
                        <div className="col">
                            {patient.risk_gaps.length > 0 && (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>ICD / Description</th>
                                            <th>Answer</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {patient.risk_gaps
                                            .filter(
                                                (x) =>
                                                    gapIsPending(x) && x?.submission_answer?.answer
                                            )
                                            .sort(attestationSubmitSort)
                                            .map((x) => (
                                                <SubmitAnswerRow
                                                    key={x.is_added ? x.code : x.icd?.code}
                                                    riskGap={x}
                                                />
                                            ))}
                                    </tbody>
                                </table>
                            )}
                            {patient.risk_gaps.length === 0 && 'None'}
                        </div>
                    </div>
                    <div className="row mt-n2">
                        <div className="col">
                            <span className="float-end fw-bold me-5">{formattedAnswerCount()}</span>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col fw-bold">Attachments:</div>
                    </div>
                    <div className="row">
                        <div className="col-11 ms-2">
                            {attachments.length > 0
                                ? attachments.map((x) => (
                                      <div key={x.id}>
                                          <MediaPreview
                                              media={x}
                                              gapType={
                                                  enumValue(['gap', 'type', 'Risk Gap']) as number
                                              }
                                          />
                                      </div>
                                  ))
                                : 'None'}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => setShow(false)}
                    >
                        {roleCanSubmit ? 'Cancel' : 'Close'}
                    </button>

                    {roleCanSubmit && (
                        <button
                            type="submit"
                            className="btn btn-primary"
                            onClick={submitAttestation}
                            disabled={disabled}
                        >
                            {disabled && <i className="fa-solid fa-spin fa-spinner me-2" />}
                            Submit
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
            <ErrorModal
                show={showError}
                showSubmit={error.showSubmit}
                handleClose={() => setShowError(false)}
                handleSubmit={() => handleSubmit(true)}
                title={error.title}
                description={error.description}
            />
        </>
    );
}
